import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import PageHeading from '../../components/PageHeading';
import { MoneyCollectOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import {
  Card,
  Table,
  InputNumber,
  Button,
  message,
  Spin,
  Row,
  Col,
  Typography,
  Space,
  Alert,
  Empty
} from 'antd';
import axiosInstance from '../../utils/axios';
import MovieCard from '../../components/dashboard/MovieCard';
import CustomerSeatLayout from '../../components/reserve/CustomerSeatLayout';

const { Title, Text } = Typography;

const RowPriceUpdate = () => {
  const { showId, dateId, timeslotId } = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [screen, setScreen] = useState({});
  const [rowPrices, setRowPrices] = useState({}); // { rowId: price }
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!state?.movie || !state?.show) {
      setError("Missing show/movie data. Please navigate from Show Update page.");
      setLoading(false);
      return;
    }

    fetchSeatLayout();
  }, [showId, dateId, timeslotId, state]);

  const fetchSeatLayout = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axiosInstance.get(
        `booking/screen/${showId}/theatre/${state?.show?.theatre}/screen/${state?.show?.screen}/date/${dateId}/slot/${timeslotId}/`
      );

      setScreen({
        sections: data?.screen?.sections || [],
        bookedSeats: data?.bookedSeats || [],
        reservedSeats: [],
        reservation: data?.reservation || []
      });

      // Pre-fill current row prices (if backend returns them)
      const prices = {};
      data?.screen?.sections?.forEach(section => {
        section.blocks?.forEach(block => {
          block.rows?.forEach(row => {
            if (row.id && row.price !== undefined) {
              prices[row.id] = row.price;
            }
          });
        });
      });
      setRowPrices(prices);
    } catch (err) {
      setError("Failed to load screen layout. Please try again.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handlePriceChange = (rowId, value) => {
    setRowPrices(prev => ({
      ...prev,
      [rowId]: value
    }));
  };

  const handleSavePrices = async () => {
    if (Object.keys(rowPrices).length === 0) {
      message.warning("No rows with prices to update");
      return;
    }

    const payload = {
      show_id: showId,
      show_date_id: dateId,
      show_timeslot_id: timeslotId,
      row_prices: Object.entries(rowPrices).map(([rowId, price]) => ({
        row_id: rowId,
        price: price || 0
      }))
    };

    try {
      setLoading(true);
      await axiosInstance.patch(`booking/update-row-prices/`, payload);
      message.success("Row prices updated successfully");
      fetchSeatLayout(); // Refresh
    } catch (err) {
      message.error("Failed to update row prices");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Flatten all rows for the table
  const allRows = [];
  screen.sections?.forEach(section => {
    section.blocks?.forEach(block => {
      block.rows?.forEach(row => {
        if (row.id) {
          // Total seats: count only non-"no_seat" seats
          const totalSeats = row.seats?.filter(s => s.status !== "no_seat").length || 0;

          // Available seats: total - booked
          const bookedInRow = screen.bookedSeats?.filter(b => b.row_name === row.name).length || 0;
          const availableSeats = totalSeats - bookedInRow;

          allRows.push({
            key: row.id,
            section: section.name,
            block: block.name,
            row: row.name,
            total_seats: totalSeats,
            available_seats: availableSeats,
            current_price: row.price || rowPrices[row.id] || 0,
            row_id: row.id
          });
        }
      });
    });
  });

  const columns = [
    { title: 'Section', dataIndex: 'section', key: 'section' },
    { title: 'Block', dataIndex: 'block', key: 'block' },
    { title: 'Row', dataIndex: 'row', key: 'row' },
    { title: 'Total Seats', dataIndex: 'total_seats', key: 'total_seats', align: 'center' },
    { title: 'Available Seats', dataIndex: 'available_seats', key: 'available_seats', align: 'center' },
    {
      title: 'Row Price ()',
      key: 'price',
      align: 'center',
      render: (_, record) => (
        <InputNumber
          min={0}
          step={10}
          value={rowPrices[record.row_id] ?? record.current_price}
          onChange={(val) => handlePriceChange(record.row_id, val)}
          style={{ width: 120 }}
        />
      )
    }
  ];

  if (error) {
    return (
      <div className="dashboard">
        <PageHeading name="Update Row Prices" icon={<MoneyCollectOutlined />} />
        <Alert message="Error" description={error} type="error" showIcon />
        <Button onClick={() => navigate(-1)} style={{ marginTop: 16 }}>
          Go Back
        </Button>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <PageHeading name="Update Row Prices" icon={<MoneyCollectOutlined />} />

      <Button
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate(-1)}
        style={{ marginBottom: 16 }}
      >
        Back to Show Update
      </Button>

      {state?.movie && state?.show ? (
        <MovieCard currentMovie={state.movie} currentShow={state.show} />
      ) : (
        <Alert
          message="Warning"
          description="Movie/Show info not available. Some details may be missing."
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      <Alert
        message="Important"
        description="Update row prices only if no bookings are active for this show/date/timeslot."
        type="warning"
        showIcon
        style={{ marginBottom: 24 }}
      />

      {loading ? (
        <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />
      ) : allRows.length === 0 ? (
        <Card>
          <Empty description="No rows found in this screen" />
        </Card>
      ) : (
        <>
          {/* Seat Layout Preview */}
          <Card title="Seat Layout Preview" style={{ marginBottom: 24 }}>
            <div style={{ position: 'relative', overflowX: 'auto' }}>
              <CustomerSeatLayout
                sections={screen.sections || []}
                bookedSeats={screen.bookedSeats || []}
                reservedSeats={screen.reservedSeats || []}
                reservation={screen.reservation || []}
                selected={[]}
                onToggle={() => {}} // Disable interaction
                onRemove={() => {}}
                readOnly={true}
              />
              <div style={{
                textAlign: 'center',
                marginTop: 16,
                padding: 12,
                background: '#f5f5f5',
                borderRadius: 8,
                fontWeight: 'bold',
                color: '#333'
              }}>
                SCREEN FRONT
              </div>
            </div>
          </Card>

          <Card title="Update Row Prices" style={{ marginBottom: 24 }}>
            <Table
              dataSource={allRows}
              columns={columns}
              rowKey="row_id"
              pagination={false}
              bordered
              size="middle"
            />
          </Card>

          <Space style={{ marginTop: 24 }}>
            <Button 
              type="primary" 
              size="large"
              onClick={handleSavePrices}
              loading={loading}
            >
              Save Row Prices
            </Button>
            <Button onClick={() => navigate(-1)}>
              Cancel
            </Button>
          </Space>
        </>
      )}
    </div>
  );
};

export default RowPriceUpdate;